<template>
  <!-- With label text -->
  <div v-if="showText" class="flex items-center gap-3">
    <div
      :class="badgeClasses"
      :style="badgeStyles"
    >
      {{ initials }}
    </div>
    <span class="text-text">{{ text }}</span>
  </div>

  <!-- Badge only -->
  <div
    v-else
    :class="badgeClasses"
    :style="badgeStyles"
  >
    {{ initials }}
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

interface Props {
  text: string
  variant?: 'red' | 'blue' | 'green' | 'yellow' | 'purple'
  size?: 'small' | 'medium' | 'large' | 'xlarge'
  showText?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'blue',
  size: 'medium',
  showText: true
})

const initials = computed(() => {
  if (!props.text) return ''
  const parts = props.text.trim().split(/\s+/)
  if (parts.length === 1) {
    return parts[0].substring(0, 2).toUpperCase()
  }
  const first = parts[0].charAt(0)
  const last = parts[parts.length - 1].charAt(0)
  return (first + last).toUpperCase()
})

const badgeClasses = computed(() => {
  const sizeClasses = {
    small: 'w-6 h-6 text-xs',
    medium: 'w-8 h-8 text-xs',
    large: 'w-10 h-10 text-sm',
    xlarge: 'w-14 h-14 text-sm',
  }
  
  const variantClasses = {
    red: 'bg-badge-red text-badge-red',
    blue: 'bg-gradient-to-br from-blue-500 to-blue-600 text-white',
    green: 'bg-badge-green text-badge-green',
    yellow: 'bg-badge-yellow text-badge-yellow',
    purple: 'bg-badge-purple text-badge-purple'
  }
  
  return [
    'rounded-full flex items-center justify-center font-medium flex-shrink-0',
    sizeClasses[props.size],
    variantClasses[props.variant]
  ].join(' ')
})

const badgeStyles = computed(() => {
  return {
    aspectRatio: '1 / 1'
  }
})
</script>

<style scoped>
/* Additional custom styles if needed */
</style>
